/**
 * Mouselook camera for the M1 kickabout. While the window has focus, moving the mouse rotates the
 * follow-cam yaw (the world angle the camera faces) — no click needed. No pointer lock in v1 (§13.2),
 * so the cursor stops generating deltas at the window edge. Owns `yaw`; main feeds it to the scene
 * (camera placement) and to KeyboardInput (camera-relative movement).
 */
export class CameraControl {
  yaw = 0;
  /** look-down angle in radians (D18): 0 = level, up to ~55° top-down; mouse-up looks up (lofts kicks). */
  pitch = (30 * Math.PI) / 180;
  private focused = true;
  /** D33: mouselook is only live while the pointer is captured (pointer-lock); main wires the gate. */
  private engageGate: () => boolean = () => true;

  /** Gate mouselook on an external engage condition (pointer-lock state). Defaults to always-on. */
  setEngageGate(gate: () => boolean): void {
    this.engageGate = gate;
  }

  private ease: { from: number; to: number; startMs: number; ms: number } | null = null;

  /** FAFI Touch: is an easeTo in flight? The touch follow policy yields while an ease owns the yaw. */
  get isEasing(): boolean {
    return this.ease !== null;
  }

  /** D35: auto-ease the yaw (keeper-gain → face downfield). Any user mouse motion cancels it. */
  easeTo(targetYaw: number, ms = 600): void {
    let d = (targetYaw - this.yaw) % (Math.PI * 2);
    if (d > Math.PI) d -= Math.PI * 2;
    else if (d < -Math.PI) d += Math.PI * 2;
    this.ease = { from: this.yaw, to: this.yaw + d, startMs: performance.now(), ms };
  }
  /** Advance the ease each frame (called from the render loop). */
  frame(nowMs: number): void {
    if (!this.ease) return;
    const t = (nowMs - this.ease.startMs) / this.ease.ms;
    if (t >= 1) {
      this.yaw = this.ease.to;
      this.ease = null;
      return;
    }
    const e2 = t * t * (3 - 2 * t);
    this.yaw = this.ease.from + (this.ease.to - this.ease.from) * e2;
  }

  private frameDxAccum = 0; // D44: raw mouse dx since the last takeFrameDx() — feeds the aim-assist break test
  private frameDyAccum = 0; // D45 Item 3b: raw mouse dy — the goal precision mode moves the on-plane point
  private yawSuspended = false; // D45 Item 3b: while goal-locked the mouse moves goalAim, NOT the camera

  /** Raw mouse dx accumulated since the last call (px), then reset. Consumed once per render frame. */
  takeFrameDx(): number {
    const d = this.frameDxAccum;
    this.frameDxAccum = 0;
    return d;
  }

  /** Raw mouse dy accumulated since the last call (px), then reset (D45 Item 3b — goal precision aim). */
  takeFrameDy(): number {
    const d = this.frameDyAccum;
    this.frameDyAccum = 0;
    return d;
  }

  /** D45 Item 3b: suspend yaw/pitch updates (goal precision mode owns the mouse); deltas still accumulate. */
  setYawSuspended(v: boolean): void {
    this.yawSuspended = v;
  }

  private readonly onMove = (e: MouseEvent): void => {
    if (!this.focused || !this.engageGate()) return;
    this.frameDxAccum += e.movementX;
    this.frameDyAccum += e.movementY;
    if (this.yawSuspended) return; // goal precision mode consumes the deltas (via takeFrameDx/Dy) — the mouse
    // moves the on-plane point, not the camera, and must NOT cancel the goal-follow ease below
    if (this.ease && Math.abs(e.movementX) + Math.abs(e.movementY) > 1) this.ease = null; // user takes over (D35)
    this.yaw += e.movementX * this.sensitivity;
    const min = (-5 * Math.PI) / 180;
    const max = (55 * Math.PI) / 180;
    this.pitch = Math.min(max, Math.max(min, this.pitch + e.movementY * this.sensitivity));
  };

  /** 0..1 look-up loft input (D18): grows as the view drops below LEVEL_RAD toward straight-ahead. */
  get aimElev(): number {
    const LEVEL = (12 * Math.PI) / 180;
    const RANGE = (17 * Math.PI) / 180;
    return Math.max(0, Math.min(1, (LEVEL - this.pitch) / RANGE));
  }
  private readonly onFocus = (): void => {
    this.focused = true;
  };
  private readonly onBlur = (): void => {
    this.focused = false;
  };
  private readonly onCtx = (e: Event): void => e.preventDefault();

  constructor(
    private readonly target: Window = window,
    private readonly sensitivity = 0.004,
  ) {
    target.addEventListener('mousemove', this.onMove);
    target.addEventListener('focus', this.onFocus);
    target.addEventListener('blur', this.onBlur);
    target.addEventListener('contextmenu', this.onCtx);
  }

  dispose(): void {
    this.target.removeEventListener('mousemove', this.onMove);
    this.target.removeEventListener('focus', this.onFocus);
    this.target.removeEventListener('blur', this.onBlur);
    this.target.removeEventListener('contextmenu', this.onCtx);
  }
}
